<template>
  <div class="image-viewer-sidebar">
    <div
      v-if="viewerMode === 'viewer'"
      class="image-viewer-sidebar__header-bar-container"
    >
      <HeaderBar :modifiers="['sticky-with-nav', 'full-width']">
        <div class="image-viewer-sidebar__menu-container">
          <div class="menu__items">
            <div
              v-for="tab in tabs"
              :key="tab.label"
              class="menu-item typo-input"
            >
              <a
                v-if="showOnMobile(tab) && showOnShafts(tab) && featureEnabled(tab)"
                :class="[
                  'menu-item__link',
                  'menu-item__link--dark',
                  'menu-item__link--transparent',
                  'menu-item__link--unpadded',
                  { 'menu-item__link--selected': setTab($route?.query?.tab, tab.value) },
                ]"
                @click="selectTab(tab)"
              >
                <span>{{ tab.label }}</span>
              </a>
            </div>
          </div>
        </div>
      </HeaderBar>
    </div>
    <div class="image-viewer-sidebar__bottom-panel">
      <DescriptionTab
        v-if="selectedTab === 'description'"
        :currentImage="currentImage"
        :images="images"
        :issuePath="issuePath"
        @update-image="updateImage"
      />
      <AiInformationTab
        v-if="selectedTab === 'ai_info'"
        :currentImage="currentImage"
      />
      <DamageCalculatorTab
        v-if="selectedTab === 'damage_calculator'"
        :currentImage="currentImage"
        :damageRating="damageRating"
        :issue="issue"
        :ratio="ratio"
        :viewerMode="viewerMode"
        @calculate-damage-rating="calculateDamageRating"
        @calculate-local-damage-rating="calculateLocalDamageRating"
        @clear-damage-rating="emit('clearDamageRating')"
        @reset-damage-rating="emit('resetDamageRating')"
        @set-viewer-mode="setViewerMode"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onBeforeUnmount, watch } from 'vue';
import { screenAtLeast } from '@/functions/screen_width';
import { useRoute, useRouter } from 'vue-router';
import I18n from 'i18n-js';
import HeaderBar from '@/components/generic/header_bar.vue';
import DescriptionTab from '@/components/image_viewer/description_tab.vue';
import AiInformationTab from '@/components/image_viewer/ai_information_tab.vue';
import DamageCalculatorTab from '@/components/image_viewer/damage_calculator_tab.vue';

const route = useRoute();

const props = defineProps({
  /** The currently selected image object */
  currentImage: { type: Object, required: true },
  /** The currently selected image object */
  currentOperation: { type: Object, default: null },
  /** Calculated damage rating */
  damageRating: { type: Number, default: null },
  /** The related issue */
  issue: { type: Object, required: true },
  /** The currently selected issue internal path */
  issuePath: { type: String, required: true },
  /** A list of all issue images */
  images: { type: Array, default: () => [] },
  /** Set the current damage ratio */
  ratio: { type: Number, required: false, default: null },
  /** Set the currently selected tab */
  selectedTabIndex: { type: Number, required: true },
  /** Set the current viewer mode */
  viewerMode: { type: String, required: true },
});

const router = useRouter();
const emit = defineEmits([
  'calculateDamageRating',
  'calculateLocalDamageRating',
  'clearDamageRating',
  'resetDamageRating',
  'setAnnotationType',
  'setSelectedTabIndex',
  'setViewerMode',
  'updateImage',
]);

const selectedTab = ref(null);

const tabs = computed(() => [
  {
    value: 'description',
    label: I18n.t('issues.description'),
    annotationType: 'all',
    showOnMobile: true,
    showOnShafts: true,
    featureEnabled: true,
  },
  {
    value: 'ai_info',
    label: I18n.t('issues.ai_info'),
    annotationType: 'all',
    showOnMobile: true,
    showOnShafts: true,
    featureEnabled: true,
  },
  {
    value: 'damage_calculator',
    label: I18n.t('issues.damage_calculator'),
    annotationType: 'damage',
    showOnMobile: false,
    showOnShafts: false,
    featureEnabled: props.currentOperation?.policy?.damage_tool,
  },
]);

watch(
  () => props.selectedTabIndex,
  (selected) => {
    selectTab(tabs.value[selected]);
  }
);

watch(
  () => route?.query?.tab,
  (tab, old_tab) => {
    if (tab !== old_tab) {
      selectTab(tabs.value.find((t) => t.value === tab));
    }
  }
);

onBeforeUnmount(() => {
  router?.replace({ query: null });
});

function selectTab(tab) {
  if (!tabs.value || !tab || selectedTab.value === tab.value) {
    return;
  }

  selectedTab.value = tab.value;

  emit('setAnnotationType', tab.annotationType);

  const tabIndex = tabs.value.findIndex((t) => t?.value === tab.value);
  if (tabIndex !== props.selectedTabIndex) {
    emit('setSelectedTabIndex', tabIndex);
  }

  // TODO: We cannot change the url params while testing in jest - fix this
  if (props.issuePath !== '/testIssuePath') router.replace({ query: { tab: tab.value } });
}

function updateImage(image) {
  emit('updateImage', image);
}

function setTab(route, tab) {
  return route === tab || selectedTab.value === tab;
}

function calculateDamageRating(type) {
  emit('calculateDamageRating', type);
}

function calculateLocalDamageRating(value) {
  emit('calculateLocalDamageRating', value);
}

function setViewerMode(mode) {
  emit('setViewerMode', mode);
}

/**
 * Determine whether route is for Shafts V1
 */
const isShaft = computed(() => {
  return route?.params.shaftId && !route.path.includes('shafts_v2');
});

/**
 * Hide certain tabs on mobile
 */
function showOnMobile(tab) {
  // TODO: find a better way for testing
  return tab?.showOnMobile || screenAtLeast('medium') || window.innerWidth >= 768;
}

/**
 * Hide certain tabs on Shafts V1
 */
function showOnShafts(tab) {
  return tab?.showOnShafts || !isShaft.value;
}

/**
 * Hide certain tabs when feature flagged
 */
function featureEnabled(tab) {
  return tab?.featureEnabled;
}

</script>

<style lang="scss" scoped>
@import '@/stylesheets/global';

.image-viewer-sidebar {
  padding: 0 bu(2);

  @include breakpoint(medium) {
    padding: 0;
  }

  &__header-bar-container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }

  &__bottom-panel {
    padding: 0 bu(1);
  }

  &__menu-container {
    width: 100%;
    padding-bottom: bu(1);
    padding-left: bu(0.75);
    border-bottom: bu(0.1) solid map-get($background-color, lightest);
  }
}
</style>
